import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";
import { fetchUsersClassesOverview } from "../../../services/usersClassesService";
import {
  getAssignedClasses,
  getCurrentUser,
  isAdminUser,
} from "../../../utils/auth";

function normalizeClass(value) {
  return String(value || "").trim().toLowerCase();
}

function scopeOverview(data) {
  const currentUser = getCurrentUser();

  if (isAdminUser(currentUser)) {
    return data || { users: [], classes: [] };
  }

  const allowed = new Set(
    (getAssignedClasses(currentUser) || []).map(normalizeClass)
  );

  const users = (data?.users || []).filter((user) =>
    allowed.has(normalizeClass(user?.className))
  );

  const classes = (data?.classes || []).filter((klass) =>
    allowed.has(normalizeClass(klass?.name || klass?.className || klass?.id))
  );

  return { users, classes };
}

function SummaryChip({ label, value }) {
  return (
    <div className="ac-uc-chip">
      <span className="ac-uc-chip-value">{value}</span>
      <span className="ac-uc-chip-label">{label}</span>
    </div>
  );
}

function UCIcon({ type }) {
  if (type === "users") {
    return (
      <svg viewBox="0 0 24 24" className="ac-uc-icon-svg" aria-hidden="true">
        <circle cx="9" cy="9" r="3" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="16.5" cy="10" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M4.5 18c.9-2.7 3-4 5.5-4s4.6 1.3 5.5 4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M14.5 17.5c.6-1.8 2-2.8 4-2.8 1 0 1.8.2 2.5.7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="ac-uc-icon-svg" aria-hidden="true">
      <path
        d="M6.5 7.5h11v10h-11z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M9 5.5v2M15 5.5v2M9 12h6M9 15h4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ManageCard({ title, desc, iconType, buttonText, onClick, tone = "green" }) {
  return (
    <div className={`ac-soft-panel ac-uc-managecard ${tone === "blue" ? "is-blue" : "is-green"}`}>
      <div className="ac-uc-managecard-head">
        <div className="ac-uc-managecard-copy">
          <h3 className="ac-uc-managecard-title">{title}</h3>
          <p className="ac-uc-managecard-text">{desc}</p>
        </div>

        <div className="ac-uc-managecard-icon">
          <UCIcon type={iconType} />
        </div>
      </div>

      <button type="button" onClick={onClick} className="ac-ghostbtn ac-uc-smallbtn">
        {buttonText}
      </button>
    </div>
  );
}

export default function UsersClassesHome() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState({ users: [], classes: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const data = await fetchUsersClassesOverview();
        if (!mounted) return;
        setOverview(scopeOverview(data));
      } catch (error) {
        console.error("Users & Classes konnten nicht geladen werden:", error);
        if (!mounted) return;
        setOverview({ users: [], classes: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const stats = useMemo(() => {
    const users = overview.users || [];
    const classes = overview.classes || [];

    const totalUsers = users.length;
    const totalClasses = classes.length;
    const activeToday = users.filter((u) => u.lastActiveLabel === "Heute").length;
    const elevated = users.filter((u) => Number(u.averageStress || 0) >= 4).length;
    const activeStreaks = users.filter((u) => Number(u.streak || 0) > 0).length;
    const bestStreak = Math.max(...users.map((u) => Number(u.streak || 0)), 0);

    const classWatch =
      [...classes].sort(
        (a, b) => (b.elevated * 2 + b.total - b.activeToday) - (a.elevated * 2 + a.total - a.activeToday)
      )[0] || null;

    return {
      totalUsers,
      totalClasses,
      activeToday,
      elevated,
      activeStreaks,
      bestStreak,
      classWatch,
    };
  }, [overview]);

  return (
    <DashboardLayout title="Nutzer:innen & Klassen">
      <div className="space-y-5">
        <section className="ac-soft-panel ac-uc-hero">
          <div className="ac-uc-hero-grid">
            <div>
              <h2 className="ac-uc-hero-title">Nutzer:innen & Klassen</h2>
              <p className="ac-uc-hero-text">
                Hier verwaltest du Schüler:innen, Klassen und Zuordnungen und bekommst
                schnell einen Überblick über Aktivität, Belastung und laufende Nutzung.
              </p>

              <div className="ac-uc-chip-row">
                <SummaryChip label="Nutzer:innen" value={loading ? "…" : stats.totalUsers} />
                <SummaryChip label="Klassen" value={loading ? "…" : stats.totalClasses} />
                <SummaryChip label="aktiv heute" value={loading ? "…" : stats.activeToday} />
                <SummaryChip label="aktive Streaks" value={loading ? "…" : stats.activeStreaks} />
                <SummaryChip label="beste Streak" value={loading ? "…" : `${stats.bestStreak} Tage`} />
                <SummaryChip label="erhöhte Belastung" value={loading ? "…" : stats.elevated} />
              </div>
            </div>

            <div className="ac-uc-hero-side">
              <div className="ac-uc-acebox">
                <AceAvatar variant="motiviert1" size={56} />
                <div className="ac-uc-acebubble">
                  Ace sagt: Wenn Aktivität sinkt und Belastung steigt, erkennst du Muster
                  in der Klassenansicht oft am schnellsten.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ac-uc-managegrid">
          <ManageCard
            title="Nutzer:innen verwalten"
            desc="Suche, filtere und öffne Profile, um Aktivität, Belastung, letzte Nutzung und Aufgaben direkt im Blick zu behalten."
            iconType="users"
            buttonText="Zur Nutzer:innen-Liste"
            onClick={() => navigate("/users-classes/users")}
            tone="green"
          />

          <ManageCard
            title="Klassen verwalten"
            desc="Öffne Klassenübersichten und prüfe Entwicklungen im Zusammenhang – besonders dann, wenn mehrere Profile gleichzeitig auffällig werden."
            iconType="classes"
            buttonText="Zur Klassen-Liste"
            onClick={() => navigate("/users-classes/classes")}
            tone="blue"
          />
        </section>

        <section className="ac-soft-panel ac-uc-bottomcard">
          <div className="ac-uc-bottomcard-grid">
            <div>
              <div className="ac-uc-bottomcard-title">Dein smarter Einstieg</div>
              <div className="ac-uc-bottomcard-text">
                Für einzelne Profile startest du am besten bei den Nutzer:innen.
                Wenn du Entwicklungen im größeren Zusammenhang prüfen willst, ist die
                Klassenansicht meistens der bessere Einstieg.
              </div>
            </div>

            <div className="ac-uc-bottomcard-side">
              <AceAvatar variant="ruhig1" size={52} />
              <div className="ac-uc-bottomcard-note">
                {stats.classWatch
                  ? `Tipp: In ${stats.classWatch.name} lohnt sich aktuell ein genauer Blick – dort häufen sich auffällige Muster am stärksten.`
                  : "Tipp: Sobald Daten vorhanden sind, zeigt dir Ace hier direkt auffällige Klassen."}
              </div>
            </div>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}